import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type CrewDefinitionFormSchema, crewDefinitionSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { CrewDefinitionForm } from "@/forms/crew-definition-form";
import { client } from "@/hono";

/** Props Interface */
interface EditCrewDefinitionModalProps extends ModalProps {
	crewDefinitionId: string;
}

export function EditCrewDefinitionModal({
	isOpen,
	onClose,
	crewDefinitionId,
}: EditCrewDefinitionModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const methods = useForm<CrewDefinitionFormSchema>({
		resolver: resolver(crewDefinitionSchema),
		// Fetched at open time rather than passed in, so the form always edits what the server
		// currently holds.
		defaultValues: async () => {
			const response = await client.crew_definitions[":crewDefinitionId"].$get({
				param: { crewDefinitionId },
			});
			const json = await responseHandler(response);
			return crewDefinitionSchema.parse(json.crewDefinitionForm);
		},
	});

	/** Functions */
	async function handleOnSubmit(json: CrewDefinitionFormSchema) {
		const response = await client.crew_definitions[":crewDefinitionId"].$patch({
			param: { crewDefinitionId },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["crew-definitions"]) });
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
				title={t("planning.crews.edit_crew_definition")}
				data-cy="crew-definition-form"
			>
				<Modal.Body>
					<CrewDefinitionForm crewDefinitionId={crewDefinitionId} />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
